import { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navigation from '../../../../components/feature/Navigation';
import LanguageSelector from '../../../../components/common/LanguageSelector';
import {
  getCountryByNameSlug,
  getCitiesByCountry,
  cityNameToUrlSlug,
  type CityData
} from '../../../../utils/cityData';
import { getSEOConfig, updateSEOTags } from '../../../../i18n/utils/seo';
import type { SupportedLanguage } from '../../../../i18n/utils/language';

export default function CountryTime() {
  const { countryName } = useParams<{ countryName: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation(['worldclock', 'common']);

  const [currentTime, setCurrentTime] = useState(new Date());

  // 从URL参数获取国家信息
  const countryInfo = useMemo(() => {
    if (!countryName) return null;
    return getCountryByNameSlug(countryName);
  }, [countryName]);

  // 获取该国家的所有城市
  const cities = useMemo(() => {
    if (!countryInfo) return [];
    return getCitiesByCountry(countryInfo.countryCode);
  }, [countryInfo]);

  // 如果找不到国家数据,重定向到世界时钟页面
  useEffect(() => {
    if (countryName && !countryInfo) {
      navigate('/worldclock');
    }
  }, [countryName, countryInfo, navigate]);

  // 更新SEO配置
  useEffect(() => {
    const currentLang = i18n.language as SupportedLanguage;

    // 使用动态国家页面的SEO配置
    if (countryInfo) {
      const countryDisplayName = i18n.language.startsWith('zh') ? countryInfo.countryNameZh : countryInfo.countryName;
      const seoConfig = getSEOConfig('country', currentLang, {
        countryName: countryDisplayName,
        citiesCount: countryInfo.citiesCount
      });
      updateSEOTags(seoConfig, currentLang);
    }
  }, [i18n.language, countryInfo]);

  // 定时器更新时间
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const formatTime = (timezone: string) => {
    if (!timezone) {
      timezone = 'UTC';
    }

    try {
      return currentTime.toLocaleTimeString('en-US', {
        timeZone: timezone,
        hour12: false,
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    } catch (error) {
      console.warn(`Invalid timezone format: ${timezone}`);
      return currentTime.toLocaleTimeString('en-US', {
        timeZone: 'UTC',
        hour12: false,
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    }
  };

  const formatDate = useCallback((timezone: string) => {
    if (!timezone) {
      timezone = 'UTC';
    }

    const locale = i18n.language === 'zh' ? 'zh-CN' : 'en-US';

    try {
      return currentTime.toLocaleDateString(locale, {
        timeZone: timezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        weekday: 'long'
      });
    } catch (error) {
      console.warn(`Invalid timezone format: ${timezone}`);
      return currentTime.toLocaleDateString(locale, {
        timeZone: 'UTC',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        weekday: 'long'
      });
    }
  }, [currentTime, i18n.language]);

  const getTimeOffset = (timezone: string) => {
    if (!timezone) {
      timezone = 'UTC';
    }

    try {
      const offset = getTimezoneOffset(timezone);
      return offset >= 0 ? `UTC+${offset}` : `UTC${offset}`;
    } catch (error) {
      return 'UTC+0';
    }
  };

  const getTimezoneOffset = (timezone: string) => {
    try {
      const now = new Date();
      const utcTime = new Date(now.toLocaleString('en-US', { timeZone: 'UTC' }));
      const targetTime = new Date(now.toLocaleString('en-US', { timeZone: timezone }));
      return (targetTime.getTime() - utcTime.getTime()) / (1000 * 60 * 60);
    } catch (error) {
      return 0;
    }
  };

  const handleCityClick = (city: CityData) => {
    const citySlug = cityNameToUrlSlug(city.nameEn);
    navigate(`/worldclock/time-in-${citySlug}`);
  };

  // 如果国家数据不存在,显示加载或错误状态
  if (!countryInfo) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation />
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">
              {t('countryNotFound', { defaultValue: 'Country not found' })}
            </h2>
            <button
              onClick={() => navigate('/worldclock')}
              className="text-blue-600 hover:text-blue-800 underline"
            >
              {t('backToWorldClock', { defaultValue: 'Back to World Clock' })}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const countryDisplayName = i18n.language.startsWith('zh') ? countryInfo.countryNameZh : countryInfo.countryName;

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />

      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Back button */}
        <div className="mb-4 md:mb-6">
          <button
            onClick={() => navigate('/worldclock')}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <i className="ri-arrow-left-line mr-2"></i>
            {t('common:button.back')}
          </button>
        </div>

        {/* Country Header */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-6 md:mb-8">
          <div className="flex items-start justify-between">
            <div className="flex items-center space-x-4">
              <span className="text-5xl">{countryInfo.flag}</span>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-gray-800">
                  {t('timeIn', { defaultValue: 'Time in' })} {countryDisplayName}
                </h1>
                <p className="text-gray-600 mt-1">
                  {countryInfo.citiesCount} {t('cities', { defaultValue: 'cities' })} {t('available', { defaultValue: 'available' })}
                </p>
              </div>
            </div>

            {/* Current Time Display */}
            {cities.length > 0 && (
              <div className="text-right">
                <div className="text-3xl md:text-4xl font-mono font-bold text-blue-600 mb-2">
                  {formatTime(cities[0].timezone)}
                </div>
                <div className="text-sm text-gray-600">
                  {formatDate(cities[0].timezone)}
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  {cities[0].timezone} ({getTimeOffset(cities[0].timezone)})
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Cities Grid */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-8">
          <h3 className="text-xl font-bold text-gray-800 mb-6">
            {t('citiesIn', { defaultValue: 'Cities in' })} {countryDisplayName}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {cities.map((city) => {
              const displayName = i18n.language.startsWith('zh') ? city.nameZh : city.nameEn;

              return (
                <div
                  key={city.timezone + city.name}
                  className="bg-gray-50 rounded-xl p-6 hover:bg-gray-100 transition-colors cursor-pointer"
                  onClick={() => handleCityClick(city)}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-2">
                      <span className="text-2xl">{city.flag}</span>
                      <span className="font-semibold text-gray-800 text-lg">{displayName}</span>
                    </div>
                    <span className="text-xs text-gray-500">{getTimeOffset(city.timezone)}</span>
                  </div>
                  <div className="text-3xl font-mono font-bold text-gray-900 mb-1">
                    {formatTime(city.timezone)}
                  </div>
                  <div className="text-sm text-gray-600 mb-3">
                    {formatDate(city.timezone)}
                  </div>
                  <div className="flex justify-end">
                    <button
                      className="text-xs text-blue-600 hover:text-blue-800 font-medium flex items-center space-x-1"
                    >
                      <span>{t('viewDetails', { defaultValue: 'View details' })}</span>
                      <i className="ri-arrow-right-line"></i>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <footer className="bg-gray-100 text-gray-800 rounded-2xl p-6 md:p-8">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center space-y-6 lg:space-y-0">
            <div className="lg:w-2/3">
              <h4 className="text-lg md:text-xl font-bold text-gray-800 mb-4 md:mb-6">{t('common:footer.about')}</h4>
              <p className="text-sm text-gray-600 leading-relaxed mb-4">
                {t('common:footer.aboutDesc')}
              </p>
              <div className="space-y-3 text-sm text-gray-600">
                <div className="flex items-start space-x-3">
                  <i className="ri-timer-line text-blue-500 mt-0.5 flex-shrink-0"></i>
                  <div>
                    <span className="font-medium text-gray-900">{t('common:nav.timer')}</span> - {t('common:footer.timerDesc')}
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <i className="ri-play-circle-line text-green-500 mt-0.5 flex-shrink-0"></i>
                  <div>
                    <span className="font-medium text-gray-900">{t('common:nav.stopwatch')}</span> - {t('common:footer.stopwatchDesc')}
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <i className="ri-global-line text-purple-500 mt-0.5 flex-shrink-0"></i>
                  <div>
                    <span className="font-medium text-gray-900">{t('common:nav.worldclock')}</span> - {t('common:footer.worldClockDesc')}
                  </div>
                </div>
              </div>
              <div className="mt-4">
                <div className="flex flex-wrap items-center gap-4 text-xs text-gray-500">
                  <a href="/terms" className="hover:text-gray-700 transition-colors">{t('common:footer.termsOfService')}</a>
                  <span>|</span>
                  <a href="#" className="hover:text-gray-700 transition-colors">{t('common:footer.privacy')}</a>
                  <span>|</span>
                  <a href="mailto:mu9347mu@gmail.com?subject=Contact Us - timer.today&body=Hello timer.today team,%0D%0A%0D%0AI would like to..." className="hover:text-gray-700 transition-colors">{t('common:footer.contactUs')}</a>
                  <span>|</span>
                  <span>{t('common:footer.copyright')}</span>
                  <span className="lg:hidden">|</span>
                  <div className="lg:hidden">
                    <LanguageSelector />
                  </div>
                </div>
              </div>
            </div>

            <div className="hidden lg:flex w-px bg-gray-300 mx-6 h-40 self-center"></div>

            <div className="hidden lg:block lg:w-1/3">
              <div className="space-y-3 text-sm text-gray-600 mt-12">
                <p>world clock time zones international time </p>
                <p>世界時計 タイムゾーン 国際時間 今何時</p>
                <p>reloj mundial zonas horarias tiempo internacional qué hora</p>
                <p>Weltzeituhr Zeitzonen internationale Zeit wie spät</p>
                <p>мировые часы часовые пояса международное время который час</p>
                <LanguageSelector />
              </div>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
}
